import { StringUtils } from './StringUtils';

/**
 * Utility functions to build structured expressions for filtering, math, and logic.
 * @namespace
 */
export class ExprUtils {
  // Logical Operators
  static and(...exprs) {
    return ExprUtils.join('AND', ...exprs);
  }

  static or(...exprs) {
    return ExprUtils.join('OR', ...exprs);
  }

  static join(exprType, ...exprs) {
    exprs = exprs.filter((expr) => expr != null);
    if (exprs.length === 0) return null;
    if (exprs.length === 1) return exprs[0];
    const expr = {
      exprType: exprType,
      exprs: exprs,
    };

    return expr;
  }

  // Field Expressions
  static fieldExpr(fieldName, exprType, value, caseSensitive = true) {
    let valueType;
    if (typeof value === 'number') {
      valueType = 'NUMBER';
    } else if (typeof value === 'boolean') {
      valueType = 'BOOLEAN';
    } else if (value == null) {
      valueType = 'NULL';
    } else if (Array.isArray(value)) {
      valueType = 'LIST';
    } else if (StringUtils.isISODate(value)) {
      valueType = 'TIMESTAMP';
    } else {
      valueType = 'LITERAL';
    }

    const exprs = [{ exprType: 'VALUE', valueType: 'VARIABLE', value: fieldName }];
    if (value != null) {
      exprs.push({ exprType: 'VALUE', valueType, value });
    }

    return { exprType, exprs, caseSensitive };
  }

  static isGreaterOrEqual(arg1, arg2) {
    return this.fieldExpr(arg1, 'IS_GREATER_OR_EQUAL', arg2);
  }

  static isLessOrEqual(arg1, arg2) {
    return this.fieldExpr(arg1, 'IS_LESS_OR_EQUAL', arg2);
  }

  static effectiveDateClause(field, startDate, endDate) {
    return ExprUtils.and(ExprUtils.isGreaterOrEqual(field, startDate), ExprUtils.isLessOrEqual(field, endDate));
  }

  // Filters
  static filterModelToExpr(filterModel, fieldDefs) {
    if (filterModel == null || Object.keys(filterModel).length === 0) return null;

    // Convert boolean set filter values from strings to actual booleans
    const booleanFields = fieldDefs.filter((fieldDef) => fieldDef.type === 'BOOLEAN').map((fieldDef) => fieldDef.field);
    booleanFields.forEach((field) => {
      const filter = filterModel[field];
      if (filter?.filterType === 'set' && filter.values) {
        filter.values = filter.values.map((v) => v === 'Y');
      }
    });

    // Capture FK field names
    const fkFieldToSpec = Object.fromEntries(
      fieldDefs
        .filter((fieldDef) => fieldDef.type === 'HYPERLINK' && fieldDef.field !== 'code')
        .flatMap((fieldDef) => {
          const entries = [[fieldDef.field.replace('Id', 'Code'), { fieldDef, fkField: 'code' }]];
          if (fieldDef.hasName) {
            entries.push([fieldDef.field.replace('Id', 'Name'), { fieldDef, fkField: 'name' }]);
          }
          return entries;
        }),
    );

    const exprs = Object.entries(filterModel).map(([fieldName, filter]) => {
      let expr;
      if (filter.conditions) {
        const expr1 = ExprUtils.basicFilterToExpr(fieldName, filter.conditions[0]);
        const expr2 = ExprUtils.basicFilterToExpr(fieldName, filter.conditions[1]);
        expr = (filter.operator === 'OR' ? ExprUtils.or : ExprUtils.and)(expr1, expr2);
      } else {
        expr = ExprUtils.basicFilterToExpr(fieldName, filter);
      }

      const fkSpec = fkFieldToSpec[fieldName];
      if (!fkSpec) {
        return expr;
      }

      const idField = fkSpec.fieldDef.field;
      if (filter.type === 'blank') return ExprUtils.notExists(idField);
      if (filter.type === 'notBlank') return ExprUtils.exists(idField);

      return {
        exprType: 'IS_ONE_OF_SUBQUERY',
        exprs: [
          { exprType: 'VALUE', valueType: 'VARIABLE', value: idField },
          { exprType: 'VALUE', valueType: 'LITERAL', value: fkSpec.fieldDef.entity },
          { exprType: 'VALUE', valueType: 'LITERAL', value: fkSpec.fieldDef.module },
          ExprUtils.renameExprValue(expr, fieldName, fkSpec.fkField),
        ],
      };
    });

    return exprs.length === 1 ? exprs[0] : ExprUtils.and(...exprs);
  }

  static renameExprValue(expr, oldValue, newValue) {
    if (expr == null) {
      return null;
    }

    // Update the field name if this expression is a variable matching the old field name
    if (expr.valueType === 'VARIABLE' && expr.value === oldValue) {
      return { ...expr, value: newValue };
    }

    // Recursively update sub-expressions with the new value
    return { ...expr, exprs: expr.exprs?.map((subExpr) => ExprUtils.renameExprValue(subExpr, oldValue, newValue)) };
  }

  static basicFilterToExpr(fieldName, filter) {
    if (filter.filterType === 'set') {
      return ExprUtils.setExpr(fieldName, filter.values);
    }
    if (filter.type === 'inRange') {
      return ExprUtils.and(
        ExprUtils.fieldExpr(fieldName, 'IS_GREATER_OR_EQUAL', filter.filter),
        ExprUtils.fieldExpr(fieldName, 'IS_LESS_OR_EQUAL', filter.filterTo),
      );
    }

    const filterTypeToExprType = {
      equals: 'IS',
      notEqual: 'IS_NOT',
      blank: 'NOT_EXISTS',
      notBlank: 'EXISTS',
      greaterThan: 'IS_GREATER',
      greaterThanOrEqual: 'IS_GREATER_OR_EQUAL',
      lessThan: 'IS_LESS',
      lessThanOrEqual: 'IS_LESS_OR_EQUAL',
    };

    // Only free text typed into an AG Grid text filter ignores case; ids are strings too, so value type can't decide
    return ExprUtils.fieldExpr(
      fieldName,
      filterTypeToExprType[filter.type] ?? StringUtils.toCodeConvention(filter.type),
      filter.filter,
      filter.filterType !== 'text',
    );
  }

  // Set Expressions
  static setExpr(fieldName, set) {
    return ExprUtils.fieldExpr(fieldName, 'IS_ONE_OF', set);
  }

  static notSetExpr(fieldName, set) {
    return ExprUtils.fieldExpr(fieldName, 'IS_NOT_ONE_OF', set);
  }

  // Conditionals
  static isNot(fieldName, value) {
    return ExprUtils.fieldExpr(fieldName, 'IS_NOT', value);
  }

  static isNotOneOf(fieldName, value) {
    return ExprUtils.fieldExpr(fieldName, 'IS_NOT_ONE_OF', value);
  }

  static is(fieldName, value) {
    return ExprUtils.fieldExpr(fieldName, 'IS', value);
  }

  // Existence
  static exists(fieldName) {
    return ExprUtils.fieldExpr(fieldName, 'EXISTS');
  }

  static notExists(fieldName) {
    return ExprUtils.fieldExpr(fieldName, 'NOT_EXISTS');
  }

  // Contains
  static contains(fieldName, value) {
    return ExprUtils.fieldExpr(fieldName, 'CONTAINS', value);
  }

  static startsWith(fieldName, value) {
    return ExprUtils.fieldExpr(fieldName, 'STARTS_WITH', value);
  }

  // Where Conditions
  static whereParams(...args) {
    const exprs = [];
    let field = null;

    args.forEach((arg, index) => {
      if (index % 2 === 0) {
        field = arg;
      } else {
        let expr;
        if (arg == null) {
          expr = ExprUtils.fieldExpr(field, 'NOT_EXISTS');
        } else if (Array.isArray(arg)) {
          expr = ExprUtils.fieldExpr(field, 'IS_ONE_OF', arg);
        } else {
          expr = ExprUtils.fieldExpr(field, 'IS', arg);
        }
        exprs.push(expr);
      }
    });

    return ExprUtils.and(...exprs);
  }

  // Re-targets an expr built on an entity's fields onto a related entity's fields (e.g. 'code' -> 'soldCustCode')
  static prefixFields(expr, prefix) {
    if (expr == null) return null;
    if (expr.valueType === 'VARIABLE') {
      return { ...expr, value: prefix + StringUtils.capitalizeFirstLetter(expr.value) };
    }
    return { ...expr, exprs: expr.exprs?.map((subExpr) => ExprUtils.prefixFields(subExpr, prefix)) };
  }

  static parentFilterExpr(parentFilterFkField, parentFilterEntity, parentFilterEntityModule) {
    return {
      exprType: 'IS_PRESENT_FK',
      exprs: [
        { exprType: 'VALUE', valueType: 'VARIABLE', value: parentFilterFkField },
        { exprType: 'VALUE', valueType: 'LITERAL', value: parentFilterEntity },
        { exprType: 'VALUE', valueType: 'LITERAL', value: parentFilterEntityModule },
      ],
    };
  }
}

/**
 * @typedef {Object} Expr
 * @property {string} exprType - The type of expression (e.g., 'AND', 'IS', 'PLUS', etc.).
 * @property {Expr[] | any[]} [exprs] - Sub-expressions or values involved.
 * @property {string} [valueType] - Type of value for 'VALUE' expressions (e.g., 'VARIABLE', 'NUMBER', 'BOOLEAN').
 * @property {any} [value] - The actual value for 'VALUE' expressions.
 * @property {boolean} [caseSensitive] - Indicates if the comparison is case-sensitive.
 */
